import { fetchCalendarItem, useCalendarItem } from './useCalendarItem'
import Styles from './FullScreenPreviewWithCarousel.module.scss'
import { ChevronLeft, ChevronRight } from 'blaze-ui/icons/24'
import { useCallback, useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { useQueries } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { PostPreview } from './PostPreview'
import { FullScreenPreviewProps, TYPES_WITH_PREVIEW } from './Types'
import { NavigationButton } from './NavigationButton'
import { Header } from './Header'
import { LoadingPreview } from './LoadingPreview/LoadingPreview'
import { AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'
import { carouselClasses } from './carouselClasses'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useIsScheduledPostsV3 } from 'hooks/isScheduledPostsV3'
import { Chat } from 'components/Chat'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { useMarkFileAsViewed } from '../useMarkFileAsViewed'
import { FullScreenPreviewWithCarouselV2 } from './FullScreenPreviewWithCarouselV2'
import { FullScreenPreviewWithCarouselV3 } from './V3/FullScreenPreviewWithCarouselV3'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'

const SENTINEL_STYLE = { top: 20, left: 0, height: 1, width: '100%', position: 'absolute' as const }

const FullScreenPreviewWithCarouselV1 = ({
  id,
  startDate,
  filters,
  onClose,
  channel,
  width,
  height,
  assetType,
  loadingStateColor,
  showCarousel = true,
  updateCurrentItem = false,
  entryPoint,
  useChat = true,
}: FullScreenPreviewProps) => {
  const { markFileAsViewed } = useMarkFileAsViewed()
  const workspaceId = useCurrentWorkspaceId()
  const [initializePopState, setInitializePopState] = useState(false)
  const [canClickArrow, setCanClickArrow] = useState(true)
  const [isHeaderIntersecting, setIsHeaderIntersecting] = useState(false)
  const [currentItem, setCurrentItem] = useState({ id, startDate })
  const [isLoading, setIsLoading] = useState(false)
  const designerChatEnabled = useFlipperFlag('designer_chat_assistant')
  const showScheduledPostTargets = useIsScheduledPostsV3()
  const { isInch13Width } = useResponsiveDesign()

  const {
    data,
    isLoading: isCalendarItemLoading,
    isFetching: isCalendarItemFetching,
  } = useCalendarItem(
    {
      id: currentItem.id,
      filters,
      startDate: currentItem.startDate,
    },
    {
      cacheTime: 0,
    },
  )

  const [items, setItems] = useState<AlmanacFileCalendarItem[]>([])
  const [currentItemIndex, setCurrentItemIndex] = useState(2)
  const [directionClicked, setDirectionClicked] = useState<'left' | 'right' | null>(null)

  const almanacFile = items[currentItemIndex]
  const nextFile = items[currentItemIndex + 1]
  const previousFile = items[currentItemIndex - 1]

  useEffect(() => {
    markFileAsViewed(currentItem.id)
  }, [currentItem]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (data && !items.length) {
      setItems([...(data?.previous_files || []), data.almanac_file, ...(data?.next_files || [])])
      if (data.previous_files.length < 2) {
        setCurrentItemIndex(data.previous_files.length)
      }
    } else if (data && items.length > 0) {
      // Check if this is an update to the current item or navigation to a new item
      if (updateCurrentItem) {
        // This is just a data update for the current item, update it in place without animation
        setItems((prev) => {
          return prev.map((item, index) => {
            if (index === currentItemIndex) {
              return data.almanac_file
            }
            return item
          })
        })
      } else {
        // This is navigation to a new item, handle the carousel logic
        setItems((prev) => {
          const newPreviousFiles =
            data?.previous_files.filter((item) => !prev.map((p) => p.key).includes(item.key)) || []
          const newNextFiles = data?.next_files.filter((item) => !prev.map((p) => p.key).includes(item.key)) || []
          if (directionClicked === 'left') {
            setCurrentItemIndex(currentItemIndex + (newPreviousFiles.length - 1))
          }

          return [...newPreviousFiles, ...prev.filter((p) => p.key !== 'loading'), ...newNextFiles]
        })
      }
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  useQueries(
    [...(data?.next_files || []), ...(data?.previous_files || [])].map((item) => {
      const queryKey = calendarKeys.detailWithFilters(item.key, filters)

      return {
        queryKey,
        queryFn: () =>
          fetchCalendarItem({
            id: item.key,
            filters,
            startDate: item.post_at,
            workspaceId,
          }),
        enabled: !!item.post_at,
        staleTime: 30 * 60 * 1000, // 30 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        refetchOnMount: true,
      }
    }),
  )

  const headerRef = useRef<HTMLDivElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)
  const topSentinelRef = useRef<HTMLDivElement>(null)

  const checkIntersection = () => {
    if (headerRef.current && previewRef.current) {
      const rectA = headerRef.current.getBoundingClientRect()
      const rectB = previewRef.current.getBoundingClientRect()

      const overlap = !(
        rectA.right < rectB.left ||
        rectA.left > rectB.right ||
        rectA.bottom < rectB.top ||
        rectA.top > rectB.bottom
      )
      setIsHeaderIntersecting(overlap)
    }
  }

  const debounceArrowClick = () => {
    setCanClickArrow(false)
    setTimeout(() => setCanClickArrow(true), 350)
  }

  const handleLeftClick = useCallback(() => {
    if (previousFile && canClickArrow) {
      debounceArrowClick()
      setDirectionClicked('left')

      // Add a loading file to the beginning of the items array to maintain correct indexing, it will be removed when the previous file is loaded
      if (currentItemIndex <= 2) {
        setItems((prev) => {
          return [
            {
              title: 'Loading...',
              post_at: new Date().toISOString(),
              key: 'loading',
              image_url: '',
              width: 300,
              height: 300,
              postable: false,
              is_generating: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              is_carousel: false,
            },
            ...prev,
          ]
        })
      }

      setCurrentItem({
        id: previousFile.key,
        startDate: previousFile.post_at,
      })
    }
  }, [currentItemIndex, previousFile, canClickArrow])

  const handleRightClick = useCallback(() => {
    if (nextFile && canClickArrow) {
      debounceArrowClick()
      setDirectionClicked('right')
      setCurrentItemIndex(currentItemIndex + 1)
      setCurrentItem({
        id: nextFile.key,
        startDate: nextFile.post_at,
      })
    }
  }, [nextFile, currentItemIndex, canClickArrow])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft') {
        handleLeftClick()
      } else if (e.key === 'ArrowRight') {
        handleRightClick()
      }
    },
    [handleLeftClick, handleRightClick, onClose],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown, onClose])

  // hack to prevent back button from closing the fullscreen preview
  // without having to include it into the react router navigation history
  useEffect(() => {
    if (initializePopState) return

    setInitializePopState(true)
    window.history.pushState(null, document.title, window.location.href) // eslint-disable-line no-restricted-properties
    window.addEventListener('popstate', () => {
      window.history.pushState(null, document.title, window.location.href) // eslint-disable-line no-restricted-properties
      onClose()
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const { width: elementWidth, height: elementHeight } = previewRef.current?.getBoundingClientRect() || {
    width: 0,
    height: 0,
  }

  const showPostPreview = almanacFile?.channel && TYPES_WITH_PREVIEW.includes(almanacFile.channel)

  const { getCarouselItemClassNames } = carouselClasses(
    almanacFile,
    nextFile,
    previousFile,
    showCarousel,
    currentItemIndex,
    elementHeight,
    elementWidth,
    !!(showScheduledPostTargets && showPostPreview),
    false,
    false,
  )

  if (isCalendarItemLoading)
    return (
      <div className={Styles.fullScreenPreview}>
        <div className={Styles.loading} data-testid="loading-preview">
          <LoadingPreview
            channel={channel ?? undefined}
            onClose={onClose}
            width={width ?? undefined}
            height={height ?? undefined}
            assetType={assetType}
            color={loadingStateColor}
          />
        </div>
      </div>
    )

  const isPosted = data?.almanac_file?.status === PostStatusPropertyValue.Posted
  const showChat =
    designerChatEnabled && isInch13Width && useChat && data?.almanac_file?.asset_type === 'Design' && !isPosted

  return (
    <div className={Styles.fullScreenPreview} onScroll={checkIntersection}>
      {showChat && data?.almanac_file?.key && (
        <div className={Styles.chatContainer}>
          <Chat isOpen fileId={data.almanac_file.key} context={{ design_id: data.almanac_file.key }} />
        </div>
      )}
      <Header
        id={currentItem.id}
        startDate={currentItem.startDate}
        channel={channel ?? undefined}
        almanacFile={data?.almanac_file}
        isIntersecting={isHeaderIntersecting}
        onClose={onClose}
        ref={headerRef}
        filters={filters}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        entryPoint={entryPoint}
        onDelete={() => {
          if (previousFile) {
            setCurrentItem({
              id: previousFile.key,
              startDate: previousFile.post_at,
            })
          } else if (nextFile) {
            setCurrentItem({
              id: nextFile.key,
              startDate: nextFile.post_at,
            })
            setCurrentItemIndex(0)
          }
          setItems((prev) => {
            return prev.filter((item) => item.key !== currentItem.id)
          })
        }}
        chatOpen={showChat}
      />
      {(previousFile || isCalendarItemLoading || isCalendarItemFetching) && (
        <NavigationButton
          side="left"
          icon={ChevronLeft}
          isDisabled={!previousFile}
          onPress={handleLeftClick}
          chatOpen={showChat}
        />
      )}

      <div className={cx(Styles.carousel, { [Styles.chatOpen]: showChat })}>
        <div id="preview-sentinel" ref={topSentinelRef} style={SENTINEL_STYLE} />
        {(showCarousel ? items : [items[currentItemIndex]]).map((item, index) => {
          if (!item) return null

          const isCurrentItem = item?.key === items[currentItemIndex]?.key
          const almanacFile = isCurrentItem ? data?.almanac_file || item : item

          return (
            <div
              onScroll={index === currentItemIndex ? checkIntersection : undefined}
              key={item?.key || `item-${index}`}
              className={cx(Styles.carouselItem, getCarouselItemClassNames(index))}
            >
              <PostPreview
                almanacFile={almanacFile}
                isLoading={isCalendarItemLoading}
                ref={index === currentItemIndex ? previewRef : null}
                showVideoControls
                isActive={isCurrentItem}
                onClose={onClose}
              />
            </div>
          )
        })}
      </div>

      {(nextFile || isCalendarItemLoading || isCalendarItemFetching) && (
        <NavigationButton side="right" icon={ChevronRight} isDisabled={!nextFile} onPress={handleRightClick} />
      )}
    </div>
  )
}

export const FullScreenPreviewWithCarousel = (props: FullScreenPreviewProps) => {
  const isScheduledPostsV3 = useIsScheduledPostsV3()
  const { data: brandPlan } = useCurrentBrandPlan()

  if (brandPlan?.has_content_strategies) {
    return <FullScreenPreviewWithCarouselV3 {...props} />
  }

  return isScheduledPostsV3 ? (
    <FullScreenPreviewWithCarouselV2 {...props} />
  ) : (
    <FullScreenPreviewWithCarouselV1 {...props} />
  )
}
